<template>
  <div class="mt-3 p-3 border border-blue-200 bg-blue-50 rounded-lg">
    <h3 class="font-semibold">Mobile View</h3>

    <template v-if="isMultiPart">
      <div class="flex items-center justify-between gap-4 mt-1">
        <label class="flex items-center gap-2 select-none cursor-pointer">
          <input
            v-model="useSummary"
            type="checkbox"
            class="h-4 w-4"
            :disabled="isLoading || !summary"
          />
          <span class="font-medium">Use this short version for Mobile?</span>
        </label>
        <button
          v-if="!isLoading && (summary || errorText)"
          type="button"
          class="text-sm font-medium px-2 py-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          @click="generate(true)"
        >
          Regenerate
        </button>
      </div>
      <p class="text-sm text-gray-600 mt-1">
        Longer SMS messages are slower, less deliverable and less actionable. <br>
        Chose this option to send this shorter summary to mobile devices. Email recipients still get the full
        message. <br> Click the short version to edit it.
      </p>
      <p v-if="errorText" class="mt-2 text-sm text-red-700">{{ errorText }}</p>
    </template>

    <!-- Message shown as a received text on a phone screen -->
    <div class="max-w-sm mt-2">
      <div class="phone-panel border-2 border-gray-400 rounded-2xl bg-gray-50 shadow">
        <div class="text-xs text-center text-gray-500 border-b border-gray-200 py-1">
          {{ senderName || "Alert" }}
        </div>
        <div class="px-3 py-2">
          <div v-if="isLoading" class="phone-bubble phone-typing" aria-label="Generating summary">
            <span /><span /><span />
          </div>
          <div v-else-if="showSummary" class="phone-bubble phone-bubble-edit">
            <textarea
              ref="textarea"
              class="phone-input"
              rows="1"
              data-gramm="false"
              :value="summary"
              @input="onInput"
              @compositionend="onInput"
            />
          </div>
          <div v-else-if="message" class="phone-bubble">{{ message }}</div>
          <div v-else class="text-sm text-gray-400 italic">Your message will appear here</div>
          <div class="text-[10px] text-gray-400 mt-1 ml-1">Now</div>
        </div>
      </div>
      <template v-if="showSummary">
        <p v-if="removedChars.length > 0" class="mt-1 text-sm text-orange-600">
          Removed characters not supported in SMS: {{ removedChars.join(" ") }}
        </p>
        <AlertCharCounter :message="summary" :limit="maxSummaryChars" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAlertStore } from "../store/alertStore";
import { gsmMaxCharsForParts, gsmPartCount, gsmTruncate, toGsm } from "../helpers/gsm";

const props = defineProps<{
  message: string | null | undefined;
  portalId: number | null | undefined;
  /** Shown at the top of the phone screen, like the sender of a text */
  senderName?: string | null;
}>();

/** Editable short version of the message */
const summary = defineModel<string>("summary", { default: "" });
/** Whether SMS / push should use the summary */
const useSummary = defineModel<boolean>("useSummary", { default: false });

const alertStore = useAlertStore();
// Short version may use up to 2 SMS parts when needed (AI aims for 1)
const maxSummaryChars = gsmMaxCharsForParts(2);

const textarea = ref<HTMLTextAreaElement | null>(null);
const removedChars = ref<string[]>([]);
const isLoading = ref(false);
const errorText = ref("");
// Message text the current summary was generated from (skip repeat calls on blur)
let summarizedMessage = "";
// Ignore responses for requests superseded by a newer one
let requestId = 0;

const isMultiPart = computed(() => gsmPartCount(props.message) > 1);
// Show the editable short version only when it exists and is selected; otherwise the full message
const showSummary = computed(() => useSummary.value && !!summary.value && !isLoading.value);

// Keep the summary GSM-only and within a single SMS part
function onInput(event: Event) {
  if ((event as InputEvent).isComposing) return;

  const el = event.target as HTMLTextAreaElement;
  const raw = el.value;
  const { text: gsmText, removed } = toGsm(raw);
  const text = gsmTruncate(gsmText, maxSummaryChars);
  if (text !== raw) {
    const caret = Math.min(
      toGsm(raw.slice(0, el.selectionStart ?? raw.length)).text.length,
      text.length
    );
    el.value = text;
    el.setSelectionRange(caret, caret);
  }

  removedChars.value = removed;
  summary.value = text;
  autoSize();
}

// Grow the bubble to fit the text instead of scrolling
function autoSize() {
  const el = textarea.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

watch([summary, isLoading, showSummary], () => nextTick(autoSize), { flush: "post" });

/** Generate a summary for the current message; parent calls this when the message textarea loses focus. */
async function generate(force = false) {
  const message = props.message ?? "";
  if (!isMultiPart.value || !props.portalId) return;
  if (!force && message === summarizedMessage) return;

  const id = ++requestId;
  summarizedMessage = message;
  isLoading.value = true;
  errorText.value = "";
  removedChars.value = [];

  const result = await alertStore.summarizeAlert(props.portalId, message);
  if (id !== requestId) return;

  isLoading.value = false;
  if (result) {
    summary.value = gsmTruncate(toGsm(result).text, maxSummaryChars);
    useSummary.value = true;
  } else {
    summary.value = "";
    useSummary.value = false;
    summarizedMessage = "";
    errorText.value =
      "Could not generate a summary. The full message will be sent to all channels.";
  }
}

/** Drop the summary (e.g. after send or when the message changes from a template) */
function reset() {
  requestId++;
  summarizedMessage = "";
  isLoading.value = false;
  errorText.value = "";
  removedChars.value = [];
  summary.value = "";
  useSummary.value = false;
}

// Short messages fit in one part already - no summary needed
watch(isMultiPart, (multi) => {
  if (!multi) reset();
});

defineExpose({ generate, reset, isLoading });
</script>

<style scoped>
/* Native mobile system fonts (iOS / Android / Windows fallback) */
.phone-panel {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", Roboto, "Segoe UI",
    "Helvetica Neue", Arial, sans-serif;
}

/* Grey "received" SMS bubble with a tail on the bottom-left (matches AlertHistoryAll) */
.phone-bubble {
  position: relative;
  display: inline-block;
  max-width: 85%;
  padding: 0.5rem 0.75rem;
  border-radius: 1.1rem;
  background: #e5e5ea;
  color: #000;
  font-size: 0.95rem;
  line-height: 1.3;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.phone-bubble::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: -6px;
  width: 14px;
  height: 14px;
  background: #e5e5ea;
  border-bottom-right-radius: 12px;
}

.phone-bubble::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: -10px;
  width: 10px;
  height: 14px;
  background: #f9fafb; /* matches panel bg-gray-50 to carve the tail curve */
  border-bottom-right-radius: 8px;
}

/* Editable bubble: fixed width so the textarea has room, ring on focus */
.phone-bubble-edit {
  display: block;
  width: 85%;
}

.phone-bubble-edit:focus-within {
  box-shadow: 0 0 0 2px #3b82f6;
}

.phone-input {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  resize: none;
  overflow: hidden;
  outline: none;
  box-shadow: none;
}

/* "Typing..." dots while the summary generates */
.phone-typing {
  display: inline-flex;
  gap: 4px;
  padding: 0.7rem 0.9rem;
}

.phone-typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8e8e93;
  animation: phone-typing 1.2s infinite ease-in-out;
}

.phone-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.phone-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes phone-typing {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
}
</style>
